<template>
  <v-container fluid class="pa-0">
    <!-- Title + subtitle + Credits -->
    <div class="cs-header d-flex align-center justify-space-between mb-4">
      <div class="cs-header-title">
        <div class="text-h6 font-weight-bold">Candidate Search</div>
        <div class="body-2 text--secondary">
          Be as specific as you wish. We’ll bring you candidate profiles that match your exact needs.
        </div>
      </div>
      <v-btn variant="outlined" color="#B8860B" class="text-none cs-credits-btn" elevation="2">
        {{ credits }} Credits Remaining
        <v-icon right>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <!-- Mobile: search + filters button -->
    <div class="d-flex d-md-none cs-mobile-bar mb-4">
      <v-text-field
        v-model="filters.keyword"
        placeholder="Search candidates"
        hide-details
        density="comfortable"
        variant="outlined"
        clearable
        prepend-inner-icon="mdi-magnify"
        class="flex-1"
      />
      <v-btn class="ml-2" variant="tonal" color="primary" @click="mobileFiltersDialog = true" icon="mdi-filter-variant" />
    </div>

    <!-- Desktop filters (hidden on mobile) -->
    <v-row dense class="mb-6 d-none d-md-flex">
      <v-col cols="12" md="2">
        <v-text-field v-model="filters.country" label="Current Country" placeholder="Search" dense variant="outlined" clearable hide-details />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field v-model="filters.category" label="Category" placeholder="Search" dense variant="outlined" clearable hide-details />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field v-model="filters.position" label="Current Position" placeholder="Search" dense variant="outlined" clearable hide-details />
      </v-col>
      <v-col cols="12" md="2">
        <v-select v-model="filters.salary" :items="salaryOptions" label="Salary" placeholder="Search" dense variant="outlined" clearable hide-details />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field v-model="filters.nationality" label="Nationality" placeholder="Search" dense variant="outlined" clearable hide-details />
      </v-col>
      <v-col cols="12" md="1">
        <v-text-field v-model="filters.keyword" label="Keyword" placeholder="Search" dense variant="outlined" clearable hide-details />
      </v-col>
      <v-col cols="12" md="1">
        <v-select v-model="filters.more" :items="moreOptions" label="More" placeholder="More" dense variant="outlined" clearable hide-details />
      </v-col>
    </v-row>

    <!-- Results count -->
    <div class="mb-4">
      <span class="font-weight-medium">{{ filteredCandidates.length }}</span> Results Found
    </div>

    <!-- Candidate Cards -->
    <v-row dense>
      <v-col v-for="cand in pagedCandidates" :key="cand.id" cols="12">
        <v-card elevation="1" class="pa-4 mb-4 cs-card">
          <v-row>
            <!-- Avatar + Name -->
            <v-col cols="12" md="4" class="d-flex align-center cs-name-col">
              <v-avatar size="48" class="mr-3">
                <v-img :src="avatarPlaceholder" />
              </v-avatar>
              <div class="subtitle-1 font-weight-medium cs-name">
                {{ cand.listing.user.fullName }}
              </div>
              <div class="ml-auto d-none d-md-block green--text">
                <span style="color: green;">Last Activity: {{ formatLastActivity(cand.created_at) }}</span>
              </div>
            </v-col>

            <!-- Left details -->
            <v-col cols="12" md="4" class="cs-left">
              <div><strong>Nationality:</strong> {{ cand.nationality }}</div>
              <div><strong>Current Position:</strong> {{ cand.current_position }}</div>
              <div><strong>Desired Salary:</strong> {{ cand.salary_expectations }}</div>
              <div class="d-md-none cs-last-activity">
                <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
                <span>Last Activity: {{ formatLastActivity(cand.created_at) }}</span>
              </div>
            </v-col>

            <!-- Middle details -->
            <v-col cols="12" md="2" class="cs-mid">
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-1">mdi-map-marker</v-icon>{{ cand.current_location }}
              </div>
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-1">mdi-cake-variant</v-icon>{{ cand.work_experience }} years exp
              </div>
              <div class="d-flex align-center mb-2">
                <v-icon class="mr-1">mdi-school</v-icon>{{ cand.education_level }}
              </div>
              <div class="d-flex align-center">
                <v-icon class="mr-1">mdi-clock-outline</v-icon>{{ cand.commitment }}
              </div>
            </v-col>

            <!-- Actions -->
            <v-col cols="12" md="2" class="d-flex flex-column justify-start cs-actions">
              <v-btn outlined color="#B8860B" class="mb-2 cs-action-btn" @click="showPhone(cand)">
                <v-icon left>mdi-phone</v-icon> Show Phone Number
              </v-btn>
              <v-btn outlined color="green" class="cs-action-btn" @click="whatsapp(cand)">
                <v-icon left>mdi-whatsapp</v-icon> WhatsApp
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pagination -->
    <div class="d-flex justify-center mt-6">
      <v-pagination v-model="page" :length="totalPages" circle />
    </div>

    <!-- Mobile Filters Sheet -->
    <v-dialog v-model="mobileFiltersDialog" fullscreen transition="dialog-bottom-transition" class="d-md-none">
      <v-card>
        <v-toolbar flat>
          <v-btn icon @click="mobileFiltersDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          <v-toolbar-title>Filters</v-toolbar-title>
          <v-spacer />
          <v-btn style="text-transform: none" variant="text" color="primary" @click="mobileFiltersDialog = false">Apply</v-btn>
        </v-toolbar>
        <v-divider />
        <v-container class="pt-4">
          <v-row dense>
            <v-col cols="12">
              <v-text-field v-model="filters.country" label="Current Country" dense variant="outlined" clearable hide-details />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="filters.category" label="Category" dense variant="outlined" clearable hide-details />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="filters.position" label="Current Position" dense variant="outlined" clearable hide-details />
            </v-col>
            <v-col cols="12">
              <v-select v-model="filters.salary" :items="salaryOptions" label="Salary" dense variant="outlined" clearable hide-details />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="filters.nationality" label="Nationality" dense variant="outlined" clearable hide-details />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="filters.keyword" label="Keyword" dense variant="outlined" clearable hide-details />
            </v-col>
            <v-col cols="12">
              <v-select v-model="filters.more" :items="moreOptions" label="More" dense variant="outlined" clearable hide-details />
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </v-container>


</template>


<script setup>
import { onMounted, reactive, computed, ref } from 'vue'
import { useStore } from 'vuex'
import avatarPlaceholder from '@/assets/individual.svg'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useDisplay } from 'vuetify'
import FooterMobile from '../footer/FooterMobile.vue'

dayjs.extend(relativeTime)

const store = useStore()
const display = useDisplay()

const infoMessage = `We’ve provided a sample of candidates to help you get started. You can adjust the filters below to find the right candidates for your job.`
const credits = ref(0)
const mobileFiltersDialog = ref(false);


const filters = reactive({
  country: '',
  category: '',
  position: '',
  salary: null,
  nationality: '',
  keyword: '',
  more: null
})

const salaryOptions = [
  'SOS 1,000 – 1,999 p.m.',
  'SOS 2,000 – 3,999 p.m.',
  'SOS 4,000 – 5,999 p.m.'
]

const moreOptions = ['Education','Experience','Skills']

// pull data from store
const allCandidates = computed(() => store.getters['candidate/allCandidates'])

// filter logic
const filteredCandidates = computed(() => {
  return allCandidates.value.filter(c => {
    const match = {
      country: filters.country ? c.current_location?.toLowerCase().includes(filters.country.toLowerCase()) : true,
      category: filters.category ? c.sub_category?.title?.toLowerCase().includes(filters.category.toLowerCase()) : true,
      position: filters.position ? c.current_position?.toLowerCase().includes(filters.position.toLowerCase()) : true,
      salary: filters.salary ? String(c.salary_expectations) === String(filters.salary) : true,
      nationality: filters.nationality ? c.nationality?.toLowerCase().includes(filters.nationality.toLowerCase()) : true,
      keyword: filters.keyword ? (
        c.listing.description?.toLowerCase().includes(filters.keyword.toLowerCase()) ||
        c.cv_summary?.toLowerCase().includes(filters.keyword.toLowerCase()) ||
        c.current_company?.toLowerCase().includes(filters.keyword.toLowerCase())
      ) : true
    }
    return Object.values(match).every(Boolean)
  })
})

// pagination
const page = ref(1)
const perPage = 10
const totalPages = computed(() => Math.ceil(filteredCandidates.value.length / perPage))
const pagedCandidates = computed(() => {
  const start = (page.value - 1) * perPage
  return filteredCandidates.value.slice(start, start + perPage)
})

const totalResults = computed(() => filteredCandidates.value.length)

function formatLastActivity(date) {
  return dayjs(date).fromNow()
}

function showPhone(cand) {
  alert(`Phone: ${cand.phone_number}`)
}
function whatsapp(cand) {
  window.open(`https://wa.me/${cand.phone_number.replace('+','')}`, '_blank')
}

onMounted(() => {
  store.dispatch('candidate/fetchCandidates')
})

// const OriginalRO = window.ResizeObserver
// window.ResizeObserver = class ResizeObserver {
//   constructor(cb) {
//     this._ro = new OriginalRO(cb)
//   }
//   observe(el, options) {
//     try { this._ro.observe(el, options) }
//     catch (err) { /* swallow ResizeObserver loop errors */ }
//   }
//   unobserve(el)   { this._ro.unobserve(el) }
//   disconnect()    { this._ro.disconnect() }
// }
</script>

<style scoped>
.v-alert { border-radius: 0; }
.green-text {
    color: green;
}

/* Mobile polish only */
@media (max-width: 768px) {
  .cs-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .cs-credits-btn {
    width: 100%;
    justify-content: center;
  }
  .cs-mobile-bar .v-text-field {
    width: 100%;
  }

  .cs-card {
    padding: 12px !important;
  }
  .cs-name-col {
    align-items: center;
  }
  .cs-name {
    font-size: 1rem;
  }
  .cs-last-activity {
    margin-top: 6px;
    font-size: 0.86rem;
    color: #1b5e20;
  }

  /* Make action buttons full width on phones */
  .cs-actions .cs-action-btn {
    width: 100%;
  }

  /* Tighten spacing */
  .cs-left div,
  .cs-mid div {
    font-size: 0.95rem;
  }

  /* Dialog fullscreen already handled by prop; remove rounding if any */
  :deep(.v-dialog--fullscreen) .v-card {
    border-radius: 0 !important;
  }
}

</style>
